// src/services/subscriptionService.ts
import { logger } from '../utils/logger';

import { supabase } from './supabase';

export interface SubscriptionStatus {
  isPremium: boolean;
  expiresAt: string | null;
  daysRemaining: number | null;
}

export interface PremiumLimits {
  maxGroups: number;
  maxSessionsPerMonth: number;
  dailySwipeLimit: number | null; // null = unlimited
  canUseAdvancedFilters: boolean;
  canSeeWhoLikedYou: boolean;
  canRewind: boolean;
}

/**
 * Check if current user has active premium subscription
 */
export async function isPremium(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data: profile } = await supabase
      .from('profiles')
      .select('is_premium, premium_expires_at')
      .eq('id', user.id)
      .single();

    if (!profile?.is_premium) return false;

    // Check if subscription hasn't expired
    if (profile.premium_expires_at) {
      const expiresAt = new Date(profile.premium_expires_at);
      if (expiresAt < new Date()) {
        // Expired - update the database
        await supabase
          .from('profiles')
          .update({ is_premium: false })
          .eq('id', user.id);
        return false;
      }
    }

    return true;
  } catch (error) {
    logger.error('Error checking premium status:', error);
    return false;
  }
}

/**
 * Get detailed subscription status
 */
export async function getSubscriptionStatus(): Promise<SubscriptionStatus> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { isPremium: false, expiresAt: null, daysRemaining: null };
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('is_premium, premium_expires_at')
      .eq('id', user.id)
      .single();

    if (!profile?.is_premium) {
      return { isPremium: false, expiresAt: null, daysRemaining: null };
    }

    const expiresAt = profile.premium_expires_at;
    let daysRemaining = null;

    if (expiresAt) {
      const expires = new Date(expiresAt);
      const now = new Date();

      if (expires < now) {
        // Expired
        await supabase
          .from('profiles')
          .update({ is_premium: false })
          .eq('id', user.id);
        return { isPremium: false, expiresAt: null, daysRemaining: null };
      }

      daysRemaining = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    }

    return {
      isPremium: true,
      expiresAt,
      daysRemaining,
    };
  } catch (error) {
    logger.error('Error getting subscription status:', error);
    return { isPremium: false, expiresAt: null, daysRemaining: null };
  }
}

/**
 * Get premium limits based on subscription status
 */
export async function getPremiumLimits(): Promise<PremiumLimits> {
  const premium = await isPremium();

  if (premium) {
    return {
      maxGroups: -1, // unlimited
      maxSessionsPerMonth: -1, // unlimited
      dailySwipeLimit: null, // unlimited
      canUseAdvancedFilters: true,
      canSeeWhoLikedYou: true,
      canRewind: true,
    };
  }

  // Free tier limits
  return {
    maxGroups: 3,
    maxSessionsPerMonth: 1,
    dailySwipeLimit: 25,
    canUseAdvancedFilters: false,
    canSeeWhoLikedYou: false,
    canRewind: false,
  };
}

/**
 * Grant premium subscription (for testing or admin)
 * In production, this would be called by a webhook from Stripe/Apple/Google
 */
export async function grantPremium(
  userId: string,
  durationDays: number,
  subscriptionId?: string,
  platform?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + durationDays);

    const { error } = await supabase
      .from('profiles')
      .update({
        is_premium: true,
        premium_expires_at: expiresAt.toISOString(),
        subscription_id: subscriptionId || null,
        subscription_platform: platform || null,
      })
      .eq('id', userId);

    if (error) {
      logger.error('Error granting premium:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    logger.error('Error in grantPremium:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Revoke premium subscription
 */
export async function revokePremium(userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        is_premium: false,
        premium_expires_at: null,
        subscription_id: null,
        subscription_platform: null,
      })
      .eq('id', userId);

    if (error) {
      logger.error('Error revoking premium:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error: any) {
    logger.error('Error in revokePremium:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Check if user can create another group
 */
export async function canCreateGroup(): Promise<{ allowed: boolean; reason?: string }> {
  const limits = await getPremiumLimits();

  if (limits.maxGroups === -1) {
    return { allowed: true }; // Premium - unlimited
  }

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { allowed: false, reason: 'Not authenticated' };

    // Count user's groups where they are a member (excluding Pip's Guild Quest groups)
    const { count } = await supabase
      .from('group_members')
      .select('group_id, groups!inner(is_quest_group)', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('groups.is_quest_group', false);

    const currentGroups = count || 0;

    if (currentGroups >= limits.maxGroups) {
      return {
        allowed: false,
        reason: `Free users can only be in ${limits.maxGroups} groups. Upgrade to Premium for unlimited groups!`,
      };
    }

    return { allowed: true };
  } catch (error) {
    logger.error('Error checking group limit:', error);
    return { allowed: false, reason: 'Error checking limits' };
  }
}

/**
 * Check if user can create another session this month
 */
export async function canCreateSession(): Promise<{ allowed: boolean; reason?: string }> {
  const limits = await getPremiumLimits();

  if (limits.maxSessionsPerMonth === -1) {
    return { allowed: true }; // Premium - unlimited
  }

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { allowed: false, reason: 'Not authenticated' };

    // Count sessions created this month
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const { count } = await supabase
      .from('sessions')
      .select('*', { count: 'exact', head: true })
      .eq('host_id', user.id)
      .gte('created_at', startOfMonth.toISOString());

    const sessionsThisMonth = count || 0;

    if (sessionsThisMonth >= limits.maxSessionsPerMonth) {
      return {
        allowed: false,
        reason: `Free users can only create ${limits.maxSessionsPerMonth} session per month. Upgrade to Premium for unlimited!`,
      };
    }

    return { allowed: true };
  } catch (error) {
    logger.error('Error checking session limit:', error);
    return { allowed: false, reason: 'Error checking limits' };
  }
}